import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircle,
  CheckCircle,
  CreditCard,
  Loader2,
  Mail,
  Phone,
  Send,
  Settings,
  Tag,
  UserPlus,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { billingAPI, tagAPI } from "../../services/api";
import employeeService from "../../services/employee";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "../ui/use-toast";
import BulkUploadTab from "./BulkUploadTab";

interface TagItem {
  id: string;
  name: string;
  color: string;
}

const widgetSchema = z
  .object({
    fullName: z.string().min(2, "Full name must be at least 2 characters"),
    email: z
      .string()
      .optional()
      .refine(
        (val) =>
          !val || val === "" || z.string().email().safeParse(val).success,
        {
          message: "Please enter a valid email address",
        }
      ),
    phone: z.string().optional(),
    inviteChannelPref: z.enum(["sms", "email"], {
      required_error: "Please select a notification preference",
    }),
  })
  .refine(
    (data) => {
      if (data.inviteChannelPref === "email") {
        return data.email && data.email.trim().length > 0;
      }
      return true;
    },
    {
      message: "Email address is required when using email invitation",
      path: ["email"],
    }
  )
  .refine(
    (data) => {
      if (data.inviteChannelPref === "sms") {
        return data.phone && data.phone.trim().length > 0;
      }
      return true;
    },
    {
      message: "Phone number is required when using SMS invitation",
      path: ["phone"],
    }
  );

type WidgetFormData = z.infer<typeof widgetSchema>;

const Widget: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [usage, setUsage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [inviteMethod, setInviteMethod] = useState<"email" | "sms">("email");
  const [availableTags, setAvailableTags] = useState<TagItem[]>([]);
  const [selectedTags, setSelectedTags] = useState<TagItem[]>([]);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<WidgetFormData>({
    resolver: zodResolver(widgetSchema),
    defaultValues: {
      inviteChannelPref: "email",
    },
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usageResponse, tagsResponse] = await Promise.all([
        billingAPI.getUsage(),
        tagAPI.listTags(),
      ]);
      setUsage(usageResponse.data);
      setAvailableTags(tagsResponse.data);
    } catch (error) {
      console.error("Failed to load data:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsage = async () => {
    try {
      const response = await billingAPI.getUsage();
      setUsage(response.data);
    } catch (error) {
      console.error("Failed to load usage:", error);
    }
  };

  const toggleTag = (tag: TagItem) => {
    setSelectedTags((prev) => {
      const exists = prev.find((t) => t.id === tag.id);
      if (exists) {
        return prev.filter((t) => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  const removeTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagId));
  };

  const handleInviteMethodChange = (method: "email" | "sms") => {
    setInviteMethod(method);
    setValue("inviteChannelPref", method);
    // Clear the other field when switching
    if (method === "email") {
      setValue("phone", "");
    } else {
      setValue("email", "");
    }
  };

  const onSubmit = async (data: WidgetFormData) => {
    setIsSubmitting(true);
    try {
      const response = await employeeService.sendInvitation({
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        inviteChannelPref: data.inviteChannelPref,
        tagIds: selectedTags.map((t) => t.id),
      });

      const contactInfo =
        data.inviteChannelPref === "email" ? data.email : data.phone;
      toast({
        title: "Invitation Sent Successfully!",
        description: `Verification invitation has been sent to ${contactInfo}. They will receive a secure link to complete their verification.`,
      });

      console.log("Invitation response:", response);

      // Reload usage to get updated check count
      await loadUsage();

      setIsSubmitted(true);
      reset();
      setInviteMethod("email");
      setValue("inviteChannelPref", "email");
      setSelectedTags([]);
    } catch (error: any) {
      const errorMessage =
        error.message || "An error occurred while sending the invitation";

      toast({
        title: "Failed to Send Invitation",
        description: errorMessage,
        variant: "destructive",
      });

      // Reload usage to update UI
      await loadUsage();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 mx-auto animate-spin" />
              <p className="text-muted-foreground mt-4">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Check if user has no plan or plan expired
  // Note: We don't check remainingChecks here because user may have unlimited plan or pay-as-you-go
  const hasNoPlan =
    !usage ||
    usage.planName === "free" ||
    usage.isPlanExpired === true;

  // Check if user has growth or enterprise plan (for bulk upload feature)
  const hasBulkUploadAccess =
    usage &&
    (usage.planName === "growth" || usage.planName === "enterprise") &&
    !usage.isPlanExpired;

  // Check if user has any active plan (to show bulk upload tab)
  const hasActivePlan =
    usage && usage.planName !== "free" && !usage.isPlanExpired;

  if (hasNoPlan) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <span>No Active Plan</span>
            </CardTitle>
            <CardDescription>
              You need an active subscription plan to add employees for
              verification.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <div className="mb-6">
                <CreditCard className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">
                  {usage?.isPlanExpired
                    ? "Subscription Expired"
                    : usage?.remainingChecks === 0
                    ? "No Checks Remaining"
                    : "Subscribe to Continue"}
                </h3>
                <p className="text-muted-foreground">
                  {usage?.isPlanExpired
                    ? `Your subscription period has expired. One month has passed since your purchase date. Please renew your subscription to continue.`
                    : usage?.remainingChecks === 0
                    ? `You've used all ${
                        usage?.checksIncluded || 0
                      } checks for this period. Upgrade to get more.`
                    : "Choose a plan that fits your needs and start verifying employees."}
                </p>
              </div>
              <div className="space-y-4">
                <Button
                  onClick={() => navigate("/billing")}
                  className="w-full sm:w-auto"
                  size="lg"
                >
                  <CreditCard className="mr-2 h-5 w-5" />
                  View Plans & Subscribe
                </Button>
                <div className="text-sm text-muted-foreground">
                  Already subscribed? Your plan may need to be activated.
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto text-green-600 mb-4" />
              <h2 className="text-2xl font-bold mb-2">
                Invitation Sent Successfully!
              </h2>
              <p className="text-muted-foreground mb-6">
                The verification invitation has been sent with a secure link.
                The employee will receive the invitation via their selected
                notification method and can start the step-by-step verification
                process.
              </p>
              <Button onClick={() => setIsSubmitted(false)}>
                Send Another Invitation
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <UserPlus className="h-6 w-6" />
            <span>Add Employee for Verification</span>
          </CardTitle>
          <CardDescription>
            Send a verification invitation to a new employee. They will receive
            a secure link via their preferred notification method to complete
            their step-by-step KYC verification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasActivePlan ? (
            <Tabs defaultValue="single" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="single">Add</TabsTrigger>
                <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
              </TabsList>
              <TabsContent value="single" className="mt-6">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name *</Label>
                    <Input
                      id="fullName"
                      placeholder="Enter full name"
                      {...register("fullName")}
                      className={errors.fullName ? "border-destructive" : ""}
                    />
                    {errors.fullName && (
                      <p className="text-sm text-destructive">
                        {errors.fullName.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center space-x-2">
                      <Settings className="h-4 w-4" />
                      <span>Invitation Method *</span>
                    </Label>
                    <div className="flex gap-4">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="inviteMethod"
                          value="email"
                          checked={inviteMethod === "email"}
                          onChange={() => handleInviteMethodChange("email")}
                          className="h-4 w-4 text-primary focus:ring-primary"
                        />
                        <Mail className="h-4 w-4" />
                        <span>Email</span>
                      </label>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          name="inviteMethod"
                          value="sms"
                          checked={inviteMethod === "sms"}
                          onChange={() => handleInviteMethodChange("sms")}
                          className="h-4 w-4 text-primary focus:ring-primary"
                        />
                        <Phone className="h-4 w-4" />
                        <span>SMS</span>
                      </label>
                    </div>
                    <input type="hidden" {...register("inviteChannelPref")} />
                    {errors.inviteChannelPref && (
                      <p className="text-sm text-destructive">
                        {errors.inviteChannelPref.message}
                      </p>
                    )}
                  </div>

                  {inviteMethod === "email" && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="email"
                        className="flex items-center space-x-2"
                      >
                        <Mail className="h-4 w-4" />
                        <span>Email Address *</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="employee@company.com"
                        {...register("email")}
                        className={errors.email ? "border-destructive" : ""}
                      />
                      {errors.email && (
                        <p className="text-sm text-destructive">
                          {errors.email.message}
                        </p>
                      )}
                    </div>
                  )}

                  {inviteMethod === "sms" && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="phone"
                        className="flex items-center space-x-2"
                      >
                        <Phone className="h-4 w-4" />
                        <span>Phone Number *</span>
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+44 (555) 123-4567"
                        {...register("phone")}
                        className={errors.phone ? "border-destructive" : ""}
                      />
                      {errors.phone && (
                        <p className="text-sm text-destructive">
                          {errors.phone.message}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Tags Selection */}
                  {availableTags.length > 0 && (
                    <div className="space-y-2">
                      <Label className="flex items-center space-x-2">
                        <Tag className="h-4 w-4" />
                        <span>Tags (Optional)</span>
                      </Label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {selectedTags.map((tag) => (
                          <Badge
                            key={tag.id}
                            variant="secondary"
                            className="flex items-center gap-1 pr-1"
                            style={{
                              backgroundColor: `${tag.color}20`,
                              borderColor: tag.color,
                              color: tag.color,
                            }}
                          >
                            <span
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            {tag.name}
                            <button
                              type="button"
                              onClick={() => removeTag(tag.id)}
                              className="ml-1 hover:bg-black/10 rounded p-0.5"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                      <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-full justify-start"
                          >
                            <Tag className="h-4 w-4 mr-2" />
                            {selectedTags.length > 0
                              ? `${selectedTags.length} tag${selectedTags.length > 1 ? "s" : ""} selected`
                              : "Select tags..."}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-2" align="start">
                          <div className="space-y-1">
                            {availableTags.map((tag) => {
                              const isSelected = selectedTags.some((t) => t.id === tag.id);
                              return (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => toggleTag(tag)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors ${
                                    isSelected ? "bg-muted" : ""
                                  }`}
                                >
                                  <span
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: tag.color }}
                                  />
                                  <span className="flex-1 text-left">{tag.name}</span>
                                  {isSelected && (
                                    <CheckCircle className="h-4 w-4 text-primary" />
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending Invitation...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Verification Invitation
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
              <TabsContent value="bulk" className="mt-6">
                <BulkUploadTab hasAccess={hasBulkUploadAccess} />
              </TabsContent>
            </Tabs>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input
                  id="fullName"
                  placeholder="Enter full name"
                  {...register("fullName")}
                  className={errors.fullName ? "border-destructive" : ""}
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive">
                    {errors.fullName.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <Settings className="h-4 w-4" />
                  <span>Invitation Method *</span>
                </Label>
                <div className="flex gap-4">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inviteMethod"
                      value="email"
                      checked={inviteMethod === "email"}
                      onChange={() => handleInviteMethodChange("email")}
                      className="h-4 w-4 text-primary focus:ring-primary"
                    />
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="inviteMethod"
                      value="sms"
                      checked={inviteMethod === "sms"}
                      onChange={() => handleInviteMethodChange("sms")}
                      className="h-4 w-4 text-primary focus:ring-primary"
                    />
                    <Phone className="h-4 w-4" />
                    <span>SMS</span>
                  </label>
                </div>
                <input type="hidden" {...register("inviteChannelPref")} />
                {errors.inviteChannelPref && (
                  <p className="text-sm text-destructive">
                    {errors.inviteChannelPref.message}
                  </p>
                )}
              </div>

              {inviteMethod === "email" && (
                <div className="space-y-2">
                  <Label
                    htmlFor="email"
                    className="flex items-center space-x-2"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email Address *</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="employee@company.com"
                    {...register("email")}
                    className={errors.email ? "border-destructive" : ""}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">
                      {errors.email.message}
                    </p>
                  )}
                </div>
              )}

              {inviteMethod === "sms" && (
                <div className="space-y-2">
                  <Label
                    htmlFor="phone"
                    className="flex items-center space-x-2"
                  >
                    <Phone className="h-4 w-4" />
                    <span>Phone Number *</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+44 (555) 123-4567"
                    {...register("phone")}
                    className={errors.phone ? "border-destructive" : ""}
                  />
                  {errors.phone && (
                    <p className="text-sm text-destructive">
                      {errors.phone.message}
                    </p>
                  )}
                </div>
              )}

              {/* Tags Selection */}
              {availableTags.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center space-x-2">
                    <Tag className="h-4 w-4" />
                    <span>Tags (Optional)</span>
                  </Label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {selectedTags.map((tag) => (
                      <Badge
                        key={tag.id}
                        variant="secondary"
                        className="flex items-center gap-1 pr-1"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          borderColor: tag.color,
                          color: tag.color,
                        }}
                      >
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => removeTag(tag.id)}
                          className="ml-1 hover:bg-black/10 rounded p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                  <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full justify-start"
                      >
                        <Tag className="h-4 w-4 mr-2" />
                        {selectedTags.length > 0
                          ? `${selectedTags.length} tag${selectedTags.length > 1 ? "s" : ""} selected`
                          : "Select tags..."}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2" align="start">
                      <div className="space-y-1">
                        {availableTags.map((tag) => {
                          const isSelected = selectedTags.some((t) => t.id === tag.id);
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              onClick={() => toggleTag(tag)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors ${
                                isSelected ? "bg-muted" : ""
                              }`}
                            >
                              <span
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="flex-1 text-left">{tag.name}</span>
                              {isSelected && (
                                <CheckCircle className="h-4 w-4 text-primary" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending Invitation...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Verification Invitation
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Information Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">What happens next?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-medium text-primary">1</span>
              </div>
              <div>
                <h4 className="font-medium">Email Sent</h4>
                <p className="text-sm text-muted-foreground">
                  The employee will receive an email with a secure verification
                  link.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-medium text-primary">2</span>
              </div>
              <div>
                <h4 className="font-medium">Document Upload</h4>
                <p className="text-sm text-muted-foreground">
                  They'll upload required documents and complete identity
                  verification.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10">
                <span className="text-sm font-medium text-primary">3</span>
              </div>
              <div>
                <h4 className="font-medium">Verification Complete</h4>
                <p className="text-sm text-muted-foreground">
                  You'll be notified once their verification is complete and
                  approved.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Widget;
